import http from '@/plugins/api'
import * as types from './types'
import storage from '@/plugins/storage'
import { startLoading } from '@/helpers/loading'
import lookupStorageMap from './lookupStorageMap'

export default {
  async setLookups({ commit, dispatch, state }, keys) {
    let _keys = keys.filter(k => !state.lookups[k])
    if (_keys.length) {
      const r = await startLoading(dispatch, 'setLookups', () =>
        http.post('lookup/ByKeys', keys)
      )
      commit(types.SET_LOOKUPS, [keys, r.data])
    }
  },
  async getLookupsByGroup({ commit, dispatch, state }, { className, group }) {
    const r = await startLoading(dispatch, 'getLookupsByGroup', () =>
      http.get(`lookup/ByGroup?className=${className}&group=${group}`)
    )
    commit(types.SET_GROUPS, r.data)
  },

  async ensureStaticData({ dispatch, state, commit }) {
    if (state.staticDataLoaded) return true;

    const ok = await dispatch('loadStaticDataFromStorage');
    if (!ok) {
      await dispatch('preloadStaticData');
    } else {
      commit(types.SET_STATIC_DATA_LOADED, true);
    }
    return true;
  },

  async getLookupValuesZeus({ commit, dispatch }, key) {
    const r = await startLoading(dispatch, 'getLookupValuesZeus', () =>
      http.get(`app/GetLookupValues?key=${key}`)
    )
    commit(types.SET_LOOKUPS_ZEUS, [key, r.data])

    const sessionKey = lookupStorageMap[key]
    if (sessionKey) {
      const formatted = r.data.map(lookup => ({
        name: lookup.text,
        value: key === 'device.applayerprotocol' ? lookup.value : lookup.id,
        altValue: lookup.value
      }))
      try {
        storage.local.setItem(sessionKey, JSON.stringify(formatted))
      } catch (e) {
        console.error(`Failed to write ${key} to localStorage`, e)
      }
    }
  },
  async getInsuranceCompanies({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'getInsuranceCompanies', () =>
      http.get('lookup/InsuranceCompanies')
    )
    commit(types.SET_INSURANCE_COMPANIES, r.data)
  },
  async getOdometerUnits({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'getOdometerUnits', () =>
      http.get('serviceRequest/OdometerUnits')
    )
    commit(types.SET_ODOMETERUNITS, r.data)
  },
  async getDamagePositions({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'getDamagePositions', () =>
      http.get('lookup/GetDamagePositions')
    )
    commit(types.SET_DAMAGEPOSITIONS, r.data)
  },
  async loadTimeZonesFromStorage({ commit }) {
    const raw = await storage.local.getItem(types.SESSION_TIMEZONES);
    let timeZones = [];

    if (raw) {
      try {
        timeZones = JSON.parse(raw);
      } catch (e) {
        console.error('Failed to parse time zones from storage', e);
      }
    }
    commit(types.SET_TIMEZONES, timeZones);
  },
  async loadInsuranceCompaniesFromStorage({ commit }) {
    const raw = await storage.local.getItem(types.SESSION_INSURANCECOMPANIES);
    let companies = [];
    if (raw) {
      try { companies = JSON.parse(raw); } catch (e) { console.error(e); }
    }
    commit(types.SET_INSURANCE_COMPANIES, companies);
  },
  async loadZeusLookupsFromStorage({ commit }) {
    const lookupsZeus = {}

    for (const [lookupKey, sessionKey] of Object.entries(lookupStorageMap)) {
      try {
        const raw = await storage.local.getItem(sessionKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          lookupsZeus[lookupKey] = parsed
          commit(types.SET_LOOKUPS_ZEUS, [lookupKey, parsed])
        }
      } catch (e) {
        console.warn(`Could not parse ${lookupKey}`, e)
      }
    }

    return lookupsZeus
  },
  async loadVehicleMakesFromStorage({ commit }) {
    const raw = await storage.local.getItem(types.SESSION_VEHICLEMAKES);
    let makes = [];

    if (raw) {
      try {
        makes = JSON.parse(raw);
      } catch (e) {
        console.error('Failed to parse vehicle makes from storage', e);
      }
    }
    commit(types.SET_VEHICLE_MAKES, makes);
  },
  async loadOdometerUnitsFromStorage({ commit }) {
    const raw = await storage.local.getItem(types.SESSION_ODOMETERUNITS);
    let units = [];
    if (raw) {
      try {
        units = JSON.parse(raw);
      } catch (e) {
        console.error('Failed to parse odometer units from storage', e);
      }
    }
    commit(types.SET_ODOMETERUNITS, units);
  },
  async loadDamagePositionsFromStorage({ commit }) {
    const raw = await storage.local.getItem(types.SESSION_DAMAGEPOSITIONS);
    let positions = [];

    if (raw) {
      try {
        positions = JSON.parse(raw);
      } catch (e) {
        console.error('Failed to parse damage positions from storage', e);
      }
    }
    commit(types.SET_DAMAGEPOSITIONS, positions);
  },
  // actions.js (lookups)
  async loadStaticDataFromStorage({ commit, dispatch }) {
    try {
      const allKeys = [
        types.SESSION_TIMEZONES,
        types.SESSION_INSURANCECOMPANIES,
        types.SESSION_VEHICLEMAKES,
        types.SESSION_ODOMETERUNITS,
        types.SESSION_DAMAGEPOSITIONS
      ]
      let isFullyCached = true
      for (const key of allKeys) {
        const raw = await storage.local.getItem(key)
        if (!raw) isFullyCached = false
      }

      // Always hydrate state from cache when present
      await Promise.all([
        dispatch('loadTimeZonesFromStorage'),
        dispatch('loadInsuranceCompaniesFromStorage'),
        dispatch('loadVehicleMakesFromStorage'),
        dispatch('loadOdometerUnitsFromStorage'),
        dispatch('loadDamagePositionsFromStorage'),
        dispatch('loadZeusLookupsFromStorage')
      ])

      if (isFullyCached) {
        commit(types.SET_STATIC_DATA_LOADED, true)
        return true
      }
      return false
    } catch (err) {
      console.error('DEBUG => [lookups] Error loading static data from storage:', err)
      commit(types.RESET_STATIC_DATA)
      return false
    }
  },
  async preloadStaticData({ dispatch, commit, state }) {
    try {
      console.log('DEBUG => [lookups] Preloading static data from API...')
      await dispatch('getInsuranceCompanies')
      await dispatch('getOdometerUnits')
      await dispatch('getDamagePositions')
      await dispatch('setLookups', ['timezone', 'vehicleMakes'])

      for (const key of Object.keys(lookupStorageMap)) {
        await dispatch('getLookupValuesZeus', key)
      }

      await dispatch('configuration/getConfigurationValues', null, { root: true })

      commit(types.SET_STATIC_DATA_LOADED, true)
      console.log('DEBUG => [lookups] Static data fetched from API...')
    } catch (err) {
      console.error('DEBUG => [lookups] Failed to preload static data:', err)
      commit(types.RESET_STATIC_DATA)
      throw err
    }
  }
}
